import { Text } from "@/components/ui/baseUi/text"
import { useBoxPlanningSlots } from "@/modules/boxPlanning/hooks/useBoxPlanningSlots"
import { useBoxPlanningActions, useBoxSlotsBeingViewed } from "@/modules/boxPlanning/store/useBoxPlanningStore"
import { useBoxSlotDetails } from "@/modules/boxSlot/hooks/useBoxSlotDetails"
import { cn } from "@/modules/lib/utils"
import { BoxPlanningCalendarEvent } from "@modules/boxPlanning/components/boxPlanningCalendarEvent"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { weekdayDateFormat } from "@modules/utils/dateUtils"
import dayjs from "dayjs"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { useCallback, useEffect, useEffectEvent, useMemo } from "react"
import { useParams } from "react-router"
import { A11y, EffectCoverflow, Keyboard, Navigation, Pagination } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"

type BoxSlotListSliderProps = {
  boxSlotId: string
  className?: string
} & React.ComponentProps<"div">

export function BoxSlotListSlider({ boxSlotId, className, ...props }: BoxSlotListSliderProps) {
  const { boxProgramId } = useParams()
  const { data: boxSlot } = useBoxSlotDetails(boxSlotId)
  const boxSlotBeingViewed = useBoxSlotsBeingViewed()
  const { addBoxSlotViewed } = useBoxPlanningActions()

  const sessionStartDate = dayjs(boxSlot.startDateTime)
  const weekStart = sessionStartDate.startOf("week")
  const weekEnd = sessionStartDate.endOf("week")

  const { data: slots } = useBoxPlanningSlots(weekStart.toDate(), weekEnd.toDate(), boxProgramId)
  const language = useLanguage()

  const filteredSlots = useMemo(() => {
    if (boxProgramId) {
      return slots.filter((slot) => slot.programmationId === boxProgramId)
    } else return slots
  }, [slots, boxProgramId])

  const getIndex = () => {
    return filteredSlots.findIndex((slot) => slot.id === boxSlotId)
  }

  const getIdbyIndex = (index: number) => {
    return filteredSlots[index] ? filteredSlots[index].id : null
  }

  const onActiveIndexChange = (activeIndex: number) => {
    const id = getIdbyIndex(activeIndex)
    if (id) {
      addBoxSlotViewed(id)
    }
  }

  const relatedSlot = useCallback(
    (eventId: string) => {
      return slots.find((slot) => slot.id === eventId)
    },
    [slots],
  )

  const initializeBoxSlotViewed = useEffectEvent(() => {
    addBoxSlotViewed(boxSlotId)
  })

  useEffect(() => {
    initializeBoxSlotViewed()
  }, [])

  return (
    <div className={cn("mb-4 px-4", className)} {...props}>
      <Swiper
        effect={"coverflow"}
        grabCursor={true}
        centeredSlides={true}
        loop={false}
        slidesPerView={3}
        shortSwipes={true}
        slideToClickedSlide={true}
        onInit={(swiperCore) => {
          swiperCore.slideTo(getIndex())
        }}
        onActiveIndexChange={(swiperCore) => {
          onActiveIndexChange(swiperCore.activeIndex)
        }}
        coverflowEffect={{
          rotate: 0,
          stretch: 0,
          depth: 100,
          modifier: 2.5,
        }}
        navigation={{
          nextEl: ".custom-next-button",
          prevEl: ".custom-prev-button",
          enabled: true,
        }}
        keyboard={{
          enabled: true,
        }}
        modules={[EffectCoverflow, Pagination, Navigation, A11y, Keyboard]}
      >
        {filteredSlots.map((slot) => (
          <SwiperSlide key={slot.id}>
            <Text fontStyle="BS" className="ml-8 capitalize">
              {boxSlotBeingViewed === slot.id && dayjs(slot.startDateTime).format(weekdayDateFormat(language))}
            </Text>
            <BoxPlanningCalendarEvent
              event={slot.toCalendarEventTypeWithHour()}
              slot={relatedSlot(slot.id)}
              key={slot.id}
              canBeClicked={false}
              isOverlayed={true}
              relativePosition={true}
            />
          </SwiperSlide>
        ))}
        <div className="flex h-full w-full">
          <ChevronLeft className="custom-prev-button absolute top-[75%] left-0 z-10 -translate-y-1/2 transform cursor-pointer text-textDefault-heading" />
          <ChevronRight className="custom-next-button absolute top-[75%] right-0 z-10 -translate-y-1/2 transform cursor-pointer text-textDefault-heading" />
        </div>
      </Swiper>
    </div>
  )
}
